import { JsonViewer } from '@/components/JsonViewer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { DomainEvent } from '@rock/types';
import { get, set } from 'lodash';
import type { FC } from 'react';

type EventChangesComparisonProps = {
    event: DomainEvent;
};

/**
 * Builds a nested object from dot-notation paths and their values
 */
function buildNestedObject(paths: string[], getValue: (path: string) => unknown): Record<string, unknown> {
    const result: Record<string, unknown> = {};

    for (const path of paths) {
        const value = getValue(path);
        set(result, path, value);
    }

    return result;
}

export const EventChangesComparison: FC<EventChangesComparisonProps> = ({ event }) => {
    // Only show comparison for updated events
    if (event.changeType !== 'updated') {
        return null;
    }

    // Skip if no fields were changed
    if (event.changedFields.length === 0) {
        return null;
    }

    // Extract the previous and updated entity objects
    const previousObj = event.previous;
    const updatedObj = event.updated;

    // Build nested objects containing only the changed fields
    const oldValuesObject = buildNestedObject(event.changedFields, (path) => get(previousObj, path));
    const newValuesObject = buildNestedObject(event.changedFields, (path) => get(updatedObj, path));

    return (
        <Card>
            <CardHeader>
                <CardTitle>Changed Fields</CardTitle>
                <CardDescription>Comparison of old and new values for fields that changed.</CardDescription>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <h3 className="mb-3 text-sm font-semibold text-muted-foreground">Previous Values</h3>
                        <div className="rounded-md bg-muted p-4">
                            <JsonViewer data={oldValuesObject} />
                        </div>
                    </div>
                    <div>
                        <h3 className="mb-3 text-sm font-semibold text-muted-foreground">New Values</h3>
                        <div className="rounded-md bg-muted p-4">
                            <JsonViewer data={newValuesObject} />
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};
